import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  MessageSquare, Send, Inbox, ArrowLeft, RefreshCcw, User, 
  Clock, Mail, ChevronRight, MailOpen
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StaffPortalMessages() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('inbox');
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [replyContent, setReplyContent] = useState('');
  const [sending, setSending] = useState(false);

  const token = localStorage.getItem('staffToken');

  useEffect(() => {
    if (!token) {
      navigate('/staff-portal');
      return;
    }
    fetchData();
  }, [token, navigate]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      await Promise.all([fetchConversations(), fetchUnreadCount()]);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  const fetchConversations = async () => {
    try {
      const res = await fetch(`${API_URL}/api/messages/staff-portal/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConversations(data);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const res = await fetch(`${API_URL}/api/messages/staff-portal/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const openConversation = async (conversation) => {
    setSelectedConversation(conversation);
    try {
      const res = await fetch(`${API_URL}/api/messages/staff-portal/conversation/${conversation.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
        fetchUnreadCount(); // Update unread count after viewing
      }
    } catch (error) {
      console.error('Error fetching conversation:', error);
      toast.error('Failed to load messages');
    }
  };

  const sendReply = async () => {
    if (!replyContent.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/messages/staff-portal/reply/${selectedConversation.id}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: replyContent })
      });
      
      if (res.ok) {
        toast.success('Reply sent!');
        setReplyContent('');
        openConversation(selectedConversation); // Refresh messages
        fetchConversations(); // Update conversation list
      } else {
        toast.error('Failed to send reply');
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/staff-portal/dashboard')}>
              <ArrowLeft size={20} />
            </Button>
            <h1 className="text-lg font-semibold">Messages</h1>
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                {unreadCount}
              </span>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={fetchData}>
            <RefreshCcw size={18} />
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4">
        {selectedConversation ? (
          // Conversation View
          <Card>
            <CardHeader className="border-b pb-3">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={() => setSelectedConversation(null)}>
                  <ArrowLeft size={18} />
                </Button>
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <User size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium">
                      {selectedConversation.participant_1_type === 'student' 
                        ? selectedConversation.participant_1_name 
                        : selectedConversation.participant_2_name}
                    </p>
                    <p className="text-xs text-gray-500">Student</p>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {/* Messages */}
              <div className="h-96 overflow-y-auto p-4 space-y-4 bg-gray-50">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender_type === 'staff' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[75%] rounded-lg p-3 ${
                        msg.sender_type === 'staff'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white border shadow-sm'
                      }`}
                    >
                      {msg.subject && (
                        <p className={`text-xs font-medium mb-1 ${
                          msg.sender_type === 'staff' ? 'text-blue-100' : 'text-gray-500'
                        }`}>
                          {msg.subject}
                        </p>
                      )}
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      <p className={`text-xs mt-1 ${
                        msg.sender_type === 'staff' ? 'text-blue-100' : 'text-gray-400'
                      }`}>
                        {formatDate(msg.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Reply Input */}
              <div className="p-4 border-t bg-white">
                <div className="flex gap-2">
                  <Textarea
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    placeholder="Type your reply..."
                    className="flex-1 resize-none"
                    rows={2}
                  />
                  <Button 
                    onClick={sendReply} 
                    disabled={!replyContent.trim() || sending}
                    className="self-end"
                  >
                    <Send size={18} />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          // Conversations List
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Inbox size={20} />
                Student Messages
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {conversations.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <MessageSquare size={48} className="mx-auto mb-3 opacity-50" />
                  <p className="font-medium">No messages yet</p>
                  <p className="text-sm">Messages from students will appear here</p>
                </div>
              ) : (
                <div className="divide-y">
                  {conversations.map((conv) => {
                    const isStudent1 = conv.participant_1_type === 'student';
                    const studentName = isStudent1 ? conv.participant_1_name : conv.participant_2_name;
                    
                    return (
                      <div
                        key={conv.id}
                        onClick={() => openConversation(conv)}
                        className="p-4 hover:bg-gray-50 cursor-pointer flex items-center gap-3"
                      >
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <User size={20} className="text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-medium truncate">{studentName}</p>
                            <span className="text-xs text-gray-400">
                              {formatDate(conv.last_message_at)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 truncate">
                            {conv.last_message}
                          </p>
                        </div>
                        <ChevronRight size={18} className="text-gray-400" />
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
